/**
 * CityAutocomplete — composant autonome de recherche de ville française.
 *
 * Features :
 * - Recherche en live (debounce 180 ms) sur /api/geo/communes
 * - Tolérance accents / casse / typos (côté backend)
 * - Navigation clavier (↑ ↓ Entrée Échap)
 * - Tactile-friendly (boutons larges, dropdown rendu via portal-less)
 * - "Villes populaires" cliquables sous le champ
 * - Affichage : 📍 Nom (Département) · CP
 *
 * Usage :
 *   <CityAutocomplete
 *     value={cityName}
 *     onChange={(name, full) => setCityName(name)}
 *     placeholder="Toutes les villes"
 *     showPopular           // chip villes populaires sous le champ
 *     data-testid="city-search"
 *   />
 *
 * `onChange(name, full)` reçoit :
 *   - name : string  (nom de la ville, ex: "Lyon")
 *   - full : objet complet { nom, code, departement, code_postal, label }
 *           ou null si l'utilisateur a vidé le champ.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapPin, X, Search, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/api";

const DEBOUNCE_MS = 180;
const MIN_CHARS = 1;
const MAX_RESULTS = 8;

export default function CityAutocomplete({
  value = "",
  onChange,
  placeholder = "Ville…",
  showPopular = false,
  autoFocus = false,
  className = "",
  inputClassName = "",
  disabled = false,
  "data-testid": testid = "city-autocomplete",
}) {
  const [query, setQuery] = useState(value || "");
  const [items, setItems] = useState([]);
  const [popular, setPopular] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const reqIdRef = useRef(0);

  // Sync prop -> state si parent change la valeur
  useEffect(() => {
    if (typeof value === "string" && value !== query) setQuery(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Charge la liste des villes populaires une fois (cache localStorage 7j)
  useEffect(() => {
    if (!showPopular) return;
    const CACHE_KEY = "confilex_popular_cities_v1";
    const cached = (() => {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const { ts, data } = JSON.parse(raw);
        if (Date.now() - ts > 7 * 24 * 3600 * 1000) return null;
        return data;
      } catch { return null; }
    })();
    if (cached) { setPopular(cached); return; }
    apiClient.get("/geo/communes/popular").then((r) => {
      const items = r.data?.items || [];
      setPopular(items);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: items })); } catch { /* ignore */ }
    }).catch(() => {});
  }, [showPopular]);

  // Recherche debouncée
  const search = useCallback((q) => {
    if (q.length < MIN_CHARS) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const id = ++reqIdRef.current;
    apiClient.get(`/geo/communes?q=${encodeURIComponent(q)}&limit=${MAX_RESULTS}`)
      .then((r) => {
        if (id !== reqIdRef.current) return; // requête obsolète
        setItems(r.data?.items || []);
        setHighlight(0);
      })
      .catch(() => { if (id === reqIdRef.current) setItems([]); })
      .finally(() => { if (id === reqIdRef.current) setLoading(false); });
  }, []);

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(v.trim()), DEBOUNCE_MS);
    // Si l'utilisateur vide le champ, notifie le parent
    if (!v.trim()) onChange?.("", null);
  };

  const select = (item) => {
    setQuery(item.nom);
    setItems([]);
    setOpen(false);
    onChange?.(item.nom, item);
    inputRef.current?.blur();
  };

  const clear = () => {
    setQuery("");
    setItems([]);
    setOpen(false);
    onChange?.("", null);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(items.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      if (items[highlight]) {
        e.preventDefault();
        select(items[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Click outside → ferme
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const showDropdown = open && (items.length > 0 || (query.trim().length >= MIN_CHARS && !loading));
  const popularList = useMemo(() => (popular || []).slice(0, 10), [popular]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`} data-testid={testid}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-night/45 pointer-events-none" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={onInput}
          onFocus={() => { if (query.length >= MIN_CHARS) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          data-testid={`${testid}-input`}
          className={`w-full pl-9 pr-9 py-2.5 rounded-sm border border-sage bg-ivory text-sm text-night placeholder:text-night/45 focus:outline-none focus:border-copper transition-smooth ${inputClassName}`}
        />
        {query && !disabled && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            aria-label="Effacer la ville"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-night/45 hover:text-night hover:bg-paper"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {loading && (
          <Loader2 className="absolute right-9 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-copper animate-spin" aria-hidden="true" />
        )}
      </div>

      {showDropdown && (
        <ul
          role="listbox"
          data-testid={`${testid}-listbox`}
          className="absolute z-50 mt-1 w-full max-h-80 overflow-y-auto rounded-sm border border-sage bg-ivory shadow-lg"
        >
          {items.length === 0 ? (
            <li className="px-3 py-3 text-sm text-night/55 italic">
              {loading ? "Recherche…" : "Aucune commune trouvée."}
            </li>
          ) : (
            items.map((it, i) => {
              const active = i === highlight;
              return (
                <li
                  key={`${it.code}-${i}`}
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => { e.preventDefault(); select(it); }}
                  data-testid={`${testid}-option-${i}`}
                  className={`px-3 py-2.5 cursor-pointer flex items-start gap-2 border-b border-sage/40 last:border-0 ${
                    active ? "bg-paper" : "hover:bg-paper/60"
                  }`}
                >
                  <MapPin className="w-3.5 h-3.5 mt-0.5 text-copper shrink-0" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-night truncate">
                      <b>{it.nom}</b>
                      {it.departement && (
                        <span className="text-night/60 font-normal"> · {it.departement}</span>
                      )}
                    </p>
                    {it.code_postal && (
                      <p className="text-[11px] text-night/50">{it.code_postal}</p>
                    )}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      )}

      {showPopular && popularList.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testid}-popular`}>
          <span className="text-[10px] uppercase tracking-[0.2em] text-night/45 self-center mr-1">Villes populaires :</span>
          {popularList.map((p) => (
            <button
              key={p.code}
              type="button"
              onClick={() => select(p)}
              data-testid={`${testid}-popular-${p.code}`}
              className="text-[11px] px-2.5 py-1 rounded-full border border-sage text-night/75 hover:border-copper hover:text-copper-deep hover:bg-paper transition-smooth"
            >
              {p.nom}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
